import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import logger from '../../../utils/logger';
import { archiveService } from '../../../services/archiveService';
import DataTable from '../../../ui/DataTable/DataTable';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { CellId, CellMuted, CellStatus, toneClass } from '../../../ui/DataTable/cells';
import { Input } from '../../../ui/Field/Field';
import { useAlert } from '../../../hooks/useAlert';
import Button from '../../../ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { adminService } from '../../../services/adminService';
import ContactPhone from '../../../components/ContactPhone';
import { echeckCorrections, correctionFp } from '../../../services/echeckCorrections';
import { EMPTY_ENRICHMENT, loadHistoryEnrichment, groupSessions, timelineRows, lookupIdsFor, dayWindow, correctionsToSave, plateMatchKey, searchedPlate, INVYGO_IDS_FAILED } from './historyLens';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import { USE_CONTRACTS_V2 } from '../../../services/contractsV2';

// The contracts store (USE_CONTRACTS_V2) serves the E-Checks of a period, not the whole archive: a date alone reads
// the checks from this many days before the day to this many after (the sessions around it).
const DATE_ONLY_SPAN_DAYS = 45;
const DAY_MS = 86400000;

// E-Check corrections written in this browser session (fingerprints): the timeline recomputes
// several times per search and on every date change - each correction is written once (CON-08).
const writtenThisSession = new Set();

/**
 * "16/07/2026 18:27" -> the date, and the time under it (the timeline fits the page width). Ejar's own text
 * ("16/07/2026 18:27:05", the old AM/PM export) is shown the one way first: dd/mm/yyyy HH:MM (27 Sep 2026).
 */
const DateTime = ({ value }) => {
    const [d, t] = (formatContractDateTime(value) || String(value || '')).split(' ');
    return <>{d}{t ? <span className="block text-xs opacity-80">{t}</span> : null}</>;
};

export const ArchiveMultiSearch = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [rawResults, setRawResults] = useState([]);
    const [fetchedTerm, setFetchedTerm] = useState('');   // the term the rows were read for ('' = a day only)
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [filterDate, setFilterDate] = useState(""); // YYYY-MM-DD
    const [error, setError] = useState(null);
    const { alertError } = useAlert();
    const { currentUser } = useAuth();

    // Load saved data or URL params on mount
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const urlQuery = searchParams.get('search');
        if (urlQuery) {
            setSearchTerm(urlQuery);
            // Auto-trigger search if URL param exists
            if (!hasSearched && !loading) {
                // We need to call handleSearch but since it's defined inside, we can just set a flag 
                // or just call an internal effect. 
                // However, handleSearch depends on state. 
                // Let's us a specific effect for this.
            }
        }
        // The timeline always opens clean: no remembered search or customer (and forget one saved before)
        try { localStorage.removeItem('archiveMultiSearch_data'); } catch (e) { /* storage blocked */ }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // The sessions of the records found (E-Check only), then the details of THOSE contracts: airport /
    // Business Bay contract by id, its Invygo booking id, the saved E-Check correction. Nothing is read when
    // the tab opens, and a date-only search reads only the contracts near that day (CON-07).
    const sessions = useMemo(() => groupSessions(rawResults), [rawResults]);
    // `for` = the search the lookup belongs to; `tried` = ids asked for (never asked twice in one search,
    // even after a failure); `checked` = ids read successfully (their saved corrections are known).
    const emptyLookup = (forRows) => ({ for: forRows, index: EMPTY_ENRICHMENT, corrections: new Map(), tried: new Set(), checked: new Set(), pending: false });
    const [lookupState, setLookup] = useState(() => emptyLookup(null));
    const [lookupError, setLookupError] = useState(null);
    const lookup = useMemo(() => (lookupState.for === rawResults ? lookupState : emptyLookup(rawResults)), [lookupState, rawResults]);
    useEffect(() => {
        if (lookup.pending) return;
        const ids = lookupIdsFor(sessions, filterDate ? dayWindow(filterDate) : null).filter((id) => !lookup.tried.has(id));
        if (!ids.length) return;
        const forRows = rawResults;
        setLookup({ ...lookup, tried: new Set([...lookup.tried, ...ids]), pending: true });
        loadHistoryEnrichment(ids, { withCorrections: true })
            .then(({ index, corrections, ids: read, invygoError }) => {
                // only the Invygo ids failed: what loaded is merged in, and that is said
                setLookupError(invygoError ? INVYGO_IDS_FAILED : null);
                // merged into what earlier lookups of this search found (a date change reads only the new ones)
                setLookup((l) => (l.for !== forRows ? l : {
                    ...l,
                    index: {
                        airport: new Map([...l.index.airport, ...index.airport]),
                        contracts: new Map([...l.index.contracts, ...index.contracts]),
                        invygo: new Map([...l.index.invygo, ...index.invygo]),
                    },
                    corrections: new Map([...l.corrections, ...corrections]),
                    checked: new Set([...l.checked, ...read]),
                    pending: false,
                }));
            })
            .catch((e) => {
                logger.error('History Lens: contract details not loaded', e);
                setLookupError('Customer names and Ejar dates could not be loaded - the rows below show the E-Check archive only. Search again to retry.');
                setLookup((l) => (l.for !== forRows ? l : { ...l, pending: false }));
            });
    }, [sessions, filterDate, lookup]); // eslint-disable-line react-hooks/exhaustive-deps

    // Effect to trigger search when searchTerm is set via URL
    useEffect(() => {
        const urlQuery = searchParams.get('search');
        if (urlQuery && urlQuery === searchTerm && !hasSearched && !loading) {
            handleSearch();
        }
    }, [searchTerm, hasSearched, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Timeline rows: enrichment, Ejar's dates and the (local) day filter; on screen only the car searched when the
    // search was a plate. The E-Check corrections below are the contract's: from every car of it, whatever the day
    // (review 3 Oct 2026 - a day showing only a contract's first car saved a correction with no "to")
    const allRows = useMemo(
        () => timelineRows(sessions, { index: lookup.index, corrections: lookup.corrections }),
        [sessions, lookup]
    );
    const results = useMemo(() => {
        const rows = filterDate
            ? timelineRows(sessions, { index: lookup.index, corrections: lookup.corrections, filterDate }) : allRows;
        const plate = searchedPlate(fetchedTerm, rawResults);
        return plate ? rows.filter((it) => plateMatchKey(it.plate) === plate) : rows;
    }, [sessions, lookup, filterDate, allRows, fetchedTerm, rawResults]);

    // Save each contract whose E-Check dates differ from Ejar's once (fingerprinted): rewritten only
    // when Ejar changes them. Only after the lookup finished (its saved corrections are known - an
    // unknown one used to be rewritten on every search), once per session, at most 50 per search.
    useEffect(() => {
        if (lookup.pending || !lookup.checked.size) return;
        const by = currentUser?.email || '';
        correctionsToSave(allRows, { checkedIds: lookup.checked, written: writtenThisSession, fpOf: correctionFp }).forEach((t) => {
            const fp = correctionFp(t.contract, t.from, t.to);
            writtenThisSession.add(fp);
            echeckCorrections.save(lookup.corrections.get(t.contract), { ...t, by })
                .then((wrote) => { if (wrote) logger.info(`[History Lens] E-Check dates of ${t.contract} corrected to Ejar`); })
                .catch((e) => { writtenThisSession.delete(fp); logger.warn('E-Check correction not saved:', e); });
        });
    }, [allRows]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = async () => {
        if (!searchTerm.trim() && !filterDate) {
            alertError("Please enter a search term or select a date.", "Missing Input");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let logs = [];
            
            if (searchTerm) {
                // the whole life of every contract found (Ejar's drop-off belongs to the contract's LAST car - owner,
                // 3 Oct 2026, CC 79361), each opened and closed by the owner's rule: Ejar's open first, the close
                // whichever is first, a contract with no E-Check yet from Ejar alone (owner, 6 Oct 2026)
                logs = await archiveService.historySearch(searchTerm);
            } else if (filterDate) {
                const win = USE_CONTRACTS_V2 ? dayWindow(filterDate) : null;
                logs = win
                    ? await archiveService.loadWindow(win.start - DATE_ONLY_SPAN_DAYS * DAY_MS, win.end + DATE_ONLY_SPAN_DAYS * DAY_MS)
                    : await archiveService.load();
            }

            setRawResults(logs);
            setFetchedTerm(searchTerm);
            setHasSearched(true);
            adminService.logUserAction('SEARCH_ARCHIVE_MULTI', `Searched Archive Multi for "${searchTerm || filterDate}"`, currentUser, { query: searchTerm, date: filterDate });
        } catch (err) {
            setError("Failed to load archive data. Please check connection.");
            logger.error(err);
        } finally {
            setLoading(false);
        }
    };



    const columns = [
        {
            key: 'contractNo',
            header: 'Contract',
            cell: (contract) => (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(contract.contractNo);
                        window.open('https://ejarprod.iyelo.com:6300//app/rental/contracts', '_blank');
                    }}
                    className="hover:underline"
                    title="Click to copy and open in Renty"
                >
                    <CellId>{contract.contractNo}</CellId>
                </button>
            ),
        },
        // long names / branches wrap onto two lines: the whole timeline fits without a sideways scrollbar
        { key: 'customerName', header: 'Customer', nowrap: false, cell: (c) => <span className="block whitespace-normal leading-snug min-w-[120px]">{c.customerName}</span> },
        {
            key: 'phone',
            header: 'Phone',
            cell: (contract) => <ContactPhone contractNo={contract.contractNo} fallback={contract.phone} />,
            sortable: false,
        },
        {
            key: 'bookingNo',
            header: 'Booking',
            cell: (contract) => (contract.bookingNo && contract.bookingNo !== 'N/A' ? (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(contract.bookingNo);
                        window.open('https://dashboard.invygo.com/bookings', '_blank');
                    }}
                    className="hover:underline"
                    title="Click to copy and open Invygo"
                >
                    <CellId>{contract.bookingNo}</CellId>
                </button>
            ) : (
                <CellMuted>{contract.bookingNo}</CellMuted>
            )),
        },
        { key: 'plate', header: 'Plate', type: 'plate' },
        { key: 'model', header: 'Model', type: 'text' },
        { key: 'branch', header: 'Branch', nowrap: false, cell: (c) => <span className="block whitespace-normal leading-snug min-w-[90px]">{c.branch}</span> },
        { key: 'from', header: 'From', cell: (contract) => <span className={`ds-cell-date ${toneClass('success')}`} title={contract.fromEjar ? `Ejar: the contract's pick-up${contract.fromEjar === 'saved' ? ' (saved correction)' : ''}` : 'E-Check: when the car was checked'}><DateTime value={contract.from} /></span> },
        {
            key: 'to',
            header: 'To',
            cell: (contract) => (contract.to === 'Still Open'
                ? <CellStatus status="open" label="Still Open" tone="warning" />
                : <span className={`ds-cell-date ${toneClass('danger')}`} title={contract.toEjar ? `Ejar: the contract's drop-off${contract.toEjar === 'saved' ? ' (saved correction)' : ''}` : 'E-Check: when the car was checked'}><DateTime value={contract.to} /></span>),
        },
    ];

    return (
        <div className="space-y-4">
            <Card title="Archive Search" subtitle="Search for contracts and vehicles">
                {/* Search Controls */}
                <div className="ds-toolbar">
                    <Input
                        type="text"
                        aria-label="Search archive"
                        placeholder="Search Plate, Contract, Branch..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        prefix={<Icon glyph={ICONS.search} size="sm" />}
                        className="flex-1 min-w-[200px]"
                    />

                    <Input
                        type="date"
                        aria-label="Date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        className="w-auto"
                    />

                    <Button
                        onClick={handleSearch}
                        disabled={loading}
                        isLoading={loading}
                        variant="primary"
                    >
                        {loading ? "Searching..." : "Search"}
                    </Button>
                </div>
            </Card>

            {(error || lookupError) && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">{error || lookupError}</div>
            )}

            {hasSearched && (
                <DataTable
                    title="Results"
                    description={`Found ${results.length} contracts`}
                    columns={columns}
                    data={results}
                    rowKey={(row, index) => index}
                    loading={loading}
                    searchable={false}
                    density="compact"
                    emptyTitle="No results found"
                    emptyDescription="No results found for the provided search terms."
                />
            )}
        </div>
    );
};
